import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Settings, Bell, Workflow, Shield,
  Building2, Mail, MessageSquare, Sliders,
  ChevronRight, Check, Zap, Gauge
} from 'lucide-react';
import WorkflowSettings from './WorkflowSettings';
import NotificationSettings from './NotificationSettings';
import UserPermissions from './UserPermissions';
import DepartmentSettings from './DepartmentSettings';

const SettingsTab = ({ user }) => {
  const [activeSection, setActiveSection] = useState('workflow');
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'MAINTENANCE_MANAGER';

  const sections = [
    {
      id: 'workflow',
      label: 'Workflow & Approvals',
      icon: Workflow,
      adminOnly: true,
      description: 'Configure ticket workflows and approval processes',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      id: 'permissions',
      label: 'User Permissions',
      icon: Shield,
      adminOnly: true,
      description: 'Manage user roles and access controls',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      id: 'departments',
      label: 'Department Settings',
      icon: Building2,
      adminOnly: true,
      description: 'Configure department-specific rules and SLAs',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50'
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: Bell,
      adminOnly: false,
      description: 'Customize your notification preferences',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50'
    }
  ];

  return (
    <div className="p-6">
      <div className="grid grid-cols-4 gap-6">
        {/* Settings Navigation */}
        <div className="col-span-1 space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <nav className="space-y-2">
              {sections.map((section) => {
                if (section.adminOnly && !isAdmin) return null;
                
                const Icon = section.icon;
                const isActive = activeSection === section.id;
                
                return (
                  <motion.button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg text-sm font-medium 
                      transition-all duration-200 relative overflow-hidden
                      ${isActive 
                        ? `${section.bgColor} ${section.color}`
                        : 'text-gray-600 hover:bg-gray-50'
                      }
                    `}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <div className={`p-2 rounded-lg ${isActive ? section.bgColor : 'bg-gray-100'}`}>
                      <Icon className={`w-5 h-5 ${isActive ? section.color : 'text-gray-500'}`} />
                    </div>
                    <div className="flex-1 text-left">
                      <div>{section.label}</div>
                      <p className={`text-xs mt-0.5 ${
                        isActive ? `${section.color}/80` : 'text-gray-500'
                      }`}>
                        {section.description}
                      </p>
                    </div>
                    {isActive && (
                      <motion.div
                        layoutId="activeIndicator"
                        className="absolute right-2 w-1.5 h-8 rounded-full bg-current"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      />
                    )}
                  </motion.button>
                );
              })}
            </nav>
          </div>

          {/* Quick Stats with Enhanced Design */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
              <div className="p-1.5 bg-indigo-100 rounded-lg">
                <Gauge className="w-5 h-5 text-indigo-600" />
              </div>
              Settings Overview
            </h3>
            <div className="space-y-3">
              <div className="p-4 rounded-xl bg-blue-50 border border-blue-100 hover:bg-blue-50/80 transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-blue-100">
                      <Workflow className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-gray-900">Active Workflows</span>
                      <div className="text-xs text-blue-700">Last updated 2h ago</div>
                    </div>
                  </div>
                  <span className="text-2xl font-bold text-blue-600">8</span>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-purple-50 border border-purple-100 hover:bg-purple-50/80 transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-purple-100">
                      <Users className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-gray-900">User Roles</span>
                      <div className="text-xs text-purple-700">6 active roles</div>
                    </div>
                  </div>
                  <span className="text-2xl font-bold text-purple-600">6</span>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100 hover:bg-emerald-50/80 transition-all">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-emerald-100">
                      <Building2 className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-gray-900">Departments</span>
                      <div className="text-xs text-emerald-700">3 pending requests</div>
                    </div>
                  </div>
                  <span className="text-2xl font-bold text-emerald-600">12</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Settings Content */}
        <div className="col-span-3">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200"
            >
              {activeSection === 'workflow' && isAdmin && (
                <WorkflowSettings />
              )}
              
              {activeSection === 'permissions' && isAdmin && (
                <UserPermissions />
              )}
              
              {activeSection === 'departments' && isAdmin && (
                <DepartmentSettings />
              )}
              
              {activeSection === 'notifications' && (
                <NotificationSettings user={user} />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default SettingsTab; 